"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import type { MessageThread } from "./useMessageThread";

/** How close to the bottom still counts as "following the conversation". */
const STICK_THRESHOLD_PX = 120;

/**
 * Follows new output while the reader is at the bottom of the thread and
 * leaves them alone once they have scrolled up to read history, where a
 * "New messages" pill brings them back on request. Also keeps their place
 * when an older page lands above what they were reading.
 */
export function useStickyScroll({
  scrollerRef,
  scrollRestoreRef,
  thread,
  streamingText,
  sending,
  followKey = "",
}: {
  /** The scrolling message list. */
  scrollerRef: RefObject<HTMLDivElement | null>;
  /** Set by useMessagePoll just before an older page is prepended. */
  scrollRestoreRef: RefObject<{ height: number; top: number } | null>;
  thread: MessageThread;
  streamingText: string | null;
  sending: boolean;
  /** Anything else drawn at the bottom that should be followed when it changes (Smithers' working steps). */
  followKey?: string;
}) {
  const { msgs, stickToBottomRef, setHasUnread, markRead } = thread;
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  const jumpToLatest = useCallback(() => {
    stickToBottomRef.current = true;
    setHasUnread(false);
    scrollToBottom();
  }, [scrollToBottom, stickToBottomRef, setHasUnread]);

  /** Recomputed on every scroll — cheap, and keeps the flag off React state. */
  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_THRESHOLD_PX;
    stickToBottomRef.current = atBottom;
    // Reaching the bottom under your own steam counts as reading it.
    if (atBottom) {
      setHasUnread(false);
      markRead();
    }
  }, [markRead, scrollerRef, stickToBottomRef, setHasUnread]);

  // Follow new output only while the reader is already at the bottom.
  //
  // Streaming scrolls instantly rather than smoothly: the reveal updates
  // every frame, and each smooth scroll restarts an animation the previous
  // one never got to finish, which is what made following a reply feel like
  // it was lagging behind the text. Discrete arrivals still animate.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom(streamingText !== null ? "auto" : "smooth");
    // `sending` is a dependency so the thinking dots scroll into view too.
  }, [msgs, streamingText, sending, followKey, scrollToBottom, stickToBottomRef]);

  useLayoutEffect(() => {
    const restore = scrollRestoreRef.current;
    const scroller = scrollerRef.current;
    if (restore && scroller) scroller.scrollTop = restore.top + scroller.scrollHeight - restore.height;
    scrollRestoreRef.current = null;
  }, [msgs, scrollerRef, scrollRestoreRef]);

  return { bottomRef, handleScroll, jumpToLatest };
}
